import { DateUtils } from './date-utils';
import * as moment from 'moment';
import { SubmissionStatusIdEnum } from '../models/enum/shared/submission-status-id.enum';

export class OdataUtils {
  /**
   * @deprecated Use `odataDateFilterQueryFromFormGroup` instead.
   */
  static constructDateSearchFilterQuery(date: string, dateFieldName: string): string {
    const dateAdjustedForBackend = DateUtils.swapDayAndMonth(date);
    const startDate = moment.utc(dateAdjustedForBackend, 'MM/DD/YYYY').startOf('day');
    const endDate = startDate.clone().endOf('day');
    return `${dateFieldName} ge cast(${startDate.toISOString(
      true
    )}, Edm.DateTimeOffset) and ${dateFieldName} le cast(${endDate.toISOString(true)}, Edm.DateTimeOffset)`;
  }

  /**
   * For backend fields typed `DateOnly` (no time component). A single-value search — since
   * there's no time-of-day to bound, this is a plain equality check against the whole day
   * rather than a ge/le range built from start-of-day/end-of-day boundaries.
   * `date` is a user-entered DD/MM/YYYY string (matches the app's shorthand date convention).
   */
  static constructDateOnlySearchFilterQuery(date: string, dateFieldName: string): string {
    const dateAdjustedForBackend = DateUtils.swapDayAndMonth(date);
    const parsedDate = moment.utc(dateAdjustedForBackend, 'MM/DD/YYYY').format('YYYY-MM-DD');
    return `${dateFieldName} eq cast(${parsedDate}, Edm.Date)`;
  }

  /**
   * For backend fields typed `DateOnly` (no time component). A range filter between two
   * already-date-only values — no start-of-day/end-of-day boundary math needed, since `Edm.Date`
   * has no time to bound; `ge`/`le` on the plain date literal is already inclusive of the whole day.
   */
  static constructDateOnlyRangeFilterQuery(startDate: string, endDate: string, dateFieldName: string): string {
    const startDateFormatted = moment.utc(startDate).format('YYYY-MM-DD');
    const endDateFormatted = moment.utc(endDate).format('YYYY-MM-DD');
    return `${dateFieldName} ge cast(${startDateFormatted}, Edm.Date) and ${dateFieldName} le cast(${endDateFormatted}, Edm.Date)`;
  }

  static activeSubmissionFilterString(): string {
    return `(statusID eq ${SubmissionStatusIdEnum.Pending} or statusID eq ${SubmissionStatusIdEnum.Submitted} or statusID eq ${SubmissionStatusIdEnum.InReview})`;
  }

  /**
   * OData string literals are single-quoted, so a `'` inside the value closes the literal early
   * and the rest of the value is parsed as filter syntax. The literal escape is a doubled quote.
   */
  static escapeStringLiteral(value: string): string {
    // eslint-disable-next-line quotes
    return value.replace(/'/g, "''");
  }

  static odataFilterQueryFromFormGroup(val: any[]): string {
    return val
      .map(([key, v]) => {
        if (typeof v === 'string') {
          return `contains(tolower(${key}), tolower('${OdataUtils.escapeStringLiteral(v)}'))`;
        }
        return `${key} eq ${this.formatOdataFilterParam(v)}`;
      })
      .join(' and ');
  }

  static odataNumberFilterQueryFromFormGroup(val: any[]): string {
    return val.map(([key, v]) => `${key} eq ${v}`).join(' and ');
  }

  static odataDateFilterQueryFromFormGroup(val: any[]): string {
    return val
      .map(([dateFieldName, date]) => {
        const startDate = moment(date);
        const endDate = startDate.clone().endOf('day');
        return `${dateFieldName} ge cast(${startDate.toISOString(
          true
        )}, Edm.DateTimeOffset) and ${dateFieldName} le cast(${endDate.toISOString(true)}, Edm.DateTimeOffset)`;
      })
      .join(' and ');
  }

  static formatOdataFilterParam(val: unknown): string {
    if (typeof val === 'string') {
      return `'${val}'`;
    }
    return String(val);
  }
}
